<script setup lang="ts">
import type {
  TableArray,
  TableFields,
  TableItems
} from '@/types/component/common/table/commonTable'
import { BTable } from 'bootstrap-vue-next'
import { ref } from 'vue'

defineProps<{
  itemsTyped: TableItems<TableArray>
  fieldsTyped: TableFields<TableArray>
  currentPage?: number
  perPage?: number
  rowDetails?: boolean
  isSort?: boolean
  targetSortItems?: string[]
}>()

const sortKey = ref<string | null>(null)
const sortOrder = ref('asc')

const emit = defineEmits<{ 'sort-by': [string, string] }>()

const sortBy = (key: string) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
  emit('sort-by', sortKey.value, sortOrder.value)
}
</script>

<template>
  <BTable
    :items="itemsTyped"
    :fields="fieldsTyped"
    :currentPage="currentPage"
    :perPage="perPage"
    class="common-table"
  >
    <template v-for="field in fieldsTyped" #[`head(${field.key})`]="head" :key="field.key">
      <slot :name="`head(${field.key})`" :label="head.label">
        <div
          v-if="targetSortItems?.includes(field.key as string)"
          class="table-head"
          @click="sortBy(field.key as string)"
        >
          <span
            >{{ head.label }}
            <span v-if="isSort && sortKey === field.key"
              ><font-awesome-icon
                :icon="sortOrder === 'asc' ? ['fas', 'sort-up'] : ['fas', 'sort-down']"
            /></span>
          </span>
        </div>
        <div v-else class="table-head">
          <span>{{ head.label }}</span>
        </div>
      </slot>
    </template>
    <template v-for="field in fieldsTyped" #[`cell(${field.key})`]="row" :key="field.key">
      <slot :name="`cell(${field.key})`" :row="row">
        {{ row.value }}
      </slot>
    </template>
    <template #row-details="row" v-if="rowDetails">
      <slot :name="`row-details`" :row="row">
        {{ row.item }}
      </slot>
    </template>
  </BTable>
</template>

<style lang="scss" scoped>
.table-head {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.common-table :deep(td) {
  text-align: center;
}
.common-table :deep(th) {
  text-align: center;
  vertical-align: middle !important;
  .form-check-input {
    position: relative;
    margin-left: 0;
  }
}
</style>
